import type { ContentItem, Language } from '@kidshub/contracts';
import { useTranslation } from 'react-i18next';
import { Badge, Icon } from '../ui/kit.js';

/**
 * Where a piece of content came from, and what that costs.
 *
 * The parent knows the source; the child never does. That asymmetry is the
 * product (`SPEC.md` §2.3), and it is why the badge sits in a fixed place on
 * every card rather than appearing only when something is unusual.
 *
 * The advertising marker is not a guess about a particular video. It is a
 * property of the provider: YouTube may serve advertising inside its own
 * player, and `SPEC.md` §7.1 requires that to be visible in the parent
 * interface rather than discovered by a three-year-old. Kids Hub shows no
 * advertising of its own and integrates no advertising network; what it cannot
 * do is promise a provider's player is free of it.
 */

interface SourceBadgeProps {
  providerId: ContentItem['providerId'];
  contentType: ContentItem['contentType'];
  /** Shown when known, so an English copy is never mistaken for the Turkish one. */
  audioLanguage?: ContentItem['audioLanguage'];
  /**
   * The family's native languages. Audio in one of them reads as the ordinary
   * case; anything else is outlined, so the odd English copy stands out.
   */
  nativeLanguages?: readonly Language[];
}

/** Providers whose own player may carry advertising. */
const ADVERTISING_PROVIDERS = new Set<ContentItem['providerId']>(['YOUTUBE']);

export const SourceBadge = ({
  providerId,
  contentType,
  audioLanguage = null,
  nativeLanguages = [],
}: SourceBadgeProps) => {
  const { t } = useTranslation();

  const audioLabel =
    audioLanguage === null
      ? null
      : t('library.audioLanguage', { language: t(`language.${audioLanguage}`) });
  const isNativeAudio = audioLanguage !== null && nativeLanguages.includes(audioLanguage);

  return (
    <span className="chips">
      {audioLabel === null ? null : isNativeAudio ? (
        // The plain badge has no kit tone: it is the quiet, expected case.
        <span className="badge">{audioLabel}</span>
      ) : (
        <Badge tone="line">{audioLabel}</Badge>
      )}
      <Badge tone="line">
        {t('library.sourceAndType', {
          provider: t(`provider.${providerId}`),
          type: t(`contentType.${contentType}`),
        })}
      </Badge>
      {ADVERTISING_PROVIDERS.has(providerId) ? (
        <Badge tone="line">
          <Icon name="alert" small />
          {t('library.mayShowAdvertising')}
        </Badge>
      ) : null}
    </span>
  );
};
